import React from "react";
import { getPdpComputedState, PDPComputedState } from "../pickers/computed-state";
import { PDPAction, PDPSwarovskiProductFrontend, PDPSwarovskiProductVariantFrontend } from "../state/types";
import { mapToPDPSwarovskiProductFrontend } from "../pickers/utils";
import { useStateUpdate } from "../../../../hooks/use-state-update";
import { onAttributeClicked } from "../state/on-attribute-clicked";
import { usePdpUrlState } from "../../use-pdp-url-state";
import { pipe } from "fp-ts/function";
import { array, option } from "fp-ts";
import {
  EnrichedSwarovskiSingleProductFrontend,
  ExtractSwarovskiPageData,
  ProductDetailPageData,
  ProductDetailPageDataShared,
  ProductVariantDetailPageData,
} from "@swo/storefront";
import { VariantAttribute } from "@swo/shared";
import { useCallbackRef } from "../../../../hooks/use-callback-ref";
import { useProductVariantStateMachine } from "../state/use-product-variant-state-machine";

export type PDPState = {
  selectedProduct: PDPSwarovskiProductFrontend;
  selectedVariant: PDPSwarovskiProductVariantFrontend;
  attributesSelection: Array<VariantAttribute>;
  computed: PDPComputedState;
  linkForClickedAttribute: (clickedAttribute: VariantAttribute) => string;
};

export function useProductVariantState(
  selectedProduct: EnrichedSwarovskiSingleProductFrontend,
  page: ExtractSwarovskiPageData<ProductDetailPageData | ProductVariantDetailPageData>["page"],
  availability: ProductDetailPageDataShared["availability"],
): [PDPState, React.Dispatch<PDPAction>] {
  function getSelectedProduct() {
    return mapToPDPSwarovskiProductFrontend(selectedProduct);
  }

  const pdpSelectedProduct = getSelectedProduct();

  const pdpSelectedVariant = pipe(
    pdpSelectedProduct.variants,
    array.findFirst(v => page.__typename === "ProductVariantDetailPage" && v.sku === page.variantSku),
    option.getOrElseW(() => undefined),
  );

  const [state, dispatch] = useProductVariantStateMachine(pdpSelectedProduct, pdpSelectedVariant);

  useStateUpdate(
    () => ({ id: pdpSelectedProduct.id, key: pdpSelectedVariant?.key }),
    () => {
      dispatch({ type: "selection-changed" });

      return Promise.resolve();
    },
  );

  const computed = getPdpComputedState(state, availability);

  const linkForClickedAttribute = useCallbackRef((clickedAttribute: VariantAttribute) => {
    const nextStateIfClicked = onAttributeClicked(state, clickedAttribute);

    return nextStateIfClicked.selectedVariant.path;
  });

  usePdpUrlState(state.selectedVariant, state.variantSelectionMethod);

  return [{ ...state, computed, linkForClickedAttribute }, dispatch];
}
